import { Box } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

import {
  SOAP_LINE_BULLET_WIDTH,
  SOAP_LINE_HEIGHT_RATIO,
  soapLineFontSize,
} from './soapSections'

interface Props {
  onAdd: () => void
}

// 行ゼロの section/フィールドの空状態を「1 行」で表す。通常は淡色プレースホルダ「(なし)」、
// hover でその場が「＋ 行を追加」に入れ替わる (= クリックで新規行を開く)。
//
// 背景: 以前は「(なし)」テキスト + 別の hover 非表示「＋行を追加」ボタンの 2 段構成だったが、
// 非表示でもボタンが縦の高さを占有し「空フィールドが無駄に縦に間延びする」問題があった
// (SoapBody の行あり section 側は 2026-06-30 FB MTG で同理由により常設ボタンを撤去済み)。
// 空側も同方針に揃え、プレースホルダ自体を hover で追加アクションに化けさせて 1 行に圧縮する。
// canon: placeholder=fg.faint / hover で操作を出す (28 spec §1.1・§4)。
export const EmptyLineAddSlot = ({ onAdd }: Props) => (
  <Box
    component="button"
    type="button"
    aria-label="行を追加"
    onClick={onAdd}
    sx={{
      appearance: 'none',
      border: 'none',
      bgcolor: 'transparent',
      cursor: 'pointer',
      fontFamily: 'inherit',
      // 行全体を click 対象にする (= テキスト幅だけでなく行いっぱい). SoapLine と同じ行 hover の
      // 当たり感 (py で air + 全幅の淡い地) に揃え、どこを押しても追加に入れる.
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textAlign: 'left',
      // 本文行と同じ字下げ (左ガター幅) に揃える (= チェックボックス列の右に本文が始まる位置).
      pl: `${SOAP_LINE_BULLET_WIDTH}px`,
      mt: 0.25,
      py: 0.25,
      color: colorTokens.fg.faint,
      borderRadius: `${RADIUS.sm}px`,
      fontSize: soapLineFontSize,
      lineHeight: SOAP_LINE_HEIGHT_RATIO,
      transition: 'color 120ms, background-color 120ms',
      '&:hover': {
        color: colorTokens.fg.muted,
        bgcolor: colorTokens.overlay.hover,
      },
      '&:focus': { outline: 'none' },
      '&:focus-visible': { boxShadow: colorTokens.focus.ringNeutral },
      // 通常は「(なし)」、hover で「＋ 行を追加」に入れ替える (同じ 1 行を占有).
      '& .empty-none': { display: 'inline' },
      '& .empty-add': { display: 'none', alignItems: 'center', gap: 0.25 },
      '&:hover .empty-none': { display: 'none' },
      '&:hover .empty-add': { display: 'inline-flex' },
    }}
  >
    <Box component="span" className="empty-none">
      (なし)
    </Box>
    <Box component="span" className="empty-add">
      <AddIcon sx={{ fontSize: 16 }} />
      行を追加
    </Box>
  </Box>
)
